import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { createContext, type PropsWithChildren, useContext, useMemo, useRef } from 'react';
import { AsideContext, createAsideAtoms } from '~/state/aside';

export function createSelectionAtoms() {
  const aside = createAsideAtoms();
  const activeIdAtom = atom<string | null>(null);
  const selectedAtom = atom<string[]>([]);
  const excludedAtom = atom<string[]>([]);
  const selectedAllAtom = atom<boolean>(false);

  const selectAtom = atom(null, (get, set, id: string, exclusive = false) => {
    const isSelected = get(selectedAtom).includes(id);
    const isExcluded = get(excludedAtom).includes(id);
    const isSelectedAll = get(selectedAllAtom);

    if (exclusive) {
      set(selectedAtom, []);
    }

    if (isSelectedAll) {
      if (isExcluded) {
        set(
          excludedAtom,
          get(excludedAtom).filter((excludedId) => excludedId !== id),
        );
      } else {
        set(excludedAtom, [...get(excludedAtom), id]);
      }
    } else {
      if (isSelected) {
        if (get(selectedAtom).length === 1) {
          set(aside.closeAsideAtom, () => {
            set(selectedAtom, []);
          });
        } else {
          set(
            selectedAtom,
            get(selectedAtom).filter((selectedId) => selectedId !== id),
          );
        }
      } else {
        set(selectedAtom, [...get(selectedAtom), id]);
        set(aside.openAsideAtom);
      }
    }
    set(activeIdAtom, null);
  });
  const isCheckedAtom = atom((get) => {
    const selectedAll = get(selectedAllAtom);
    const selected = get(selectedAtom);
    const excluded = get(excludedAtom);

    return (id: string) => {
      if (selectedAll) {
        return !excluded.includes(id);
      } else {
        return selected.includes(id);
      }
    };
  });

  const isActiveAtom = atom((get) => {
    const isChecked = get(isCheckedAtom);
    return (id: string) => {
      const activeId = get(activeIdAtom);
      if (activeId === id) {
        return true;
      }

      return isChecked(id);
    };
  });

  const selectAllAtom = atom(null, (get, set) => {
    const selectedAll = get(selectedAllAtom);
    if (!selectedAll) {
      set(selectedAllAtom, true);
      set(selectedAtom, []);
      set(excludedAtom, []);
      set(aside.openAsideAtom);
    } else {
      set(aside.closeAsideAtom, () => {
        set(selectedAllAtom, false);
        set(selectedAtom, []);
        set(excludedAtom, []);
      });
    }
  });

  const resetSelectionAtom = atom(null, (get, set) => {
    set(aside.closeAsideAtom, () => {
      set(activeIdAtom, null);
      set(selectedAllAtom, false);
      set(selectedAtom, []);
      set(excludedAtom, []);
    });
  });

  const activateAtom = atom(null, (get, set, id: string) => {
    if (id === get(activeIdAtom)) {
      set(aside.closeAsideAtom, () => {
        set(activeIdAtom, null);
      });
    } else {
      set(activeIdAtom, id);
      set(aside.openAsideAtom);
    }
    set(selectedAllAtom, false);
    set(selectedAtom, []);
    set(excludedAtom, []);
  });

  return {
    activateAtom,
    activeIdAtom,
    aside,
    excludedAtom,
    isActiveAtom,
    isCheckedAtom,
    resetSelectionAtom,
    selectAtom,
    selectAllAtom,
    selectedAllAtom,
    selectedAtom,
  };
}

type SelectionAtoms = ReturnType<typeof createSelectionAtoms>;

interface SelectionContextValue {
  atoms: SelectionAtoms;
}

export const SelectionContext = createContext<SelectionContextValue | null>(null);

function useSelectionContext() {
  const ctx = useContext(SelectionContext);
  if (!ctx) throw new Error('Selection hooks must be used inside <SelectionProvider>');
  return ctx;
}

export const SelectionProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const atomsRef = useRef<SelectionAtoms | null>(null);
  if (!atomsRef.current) {
    atomsRef.current = createSelectionAtoms();
  }
  const atoms = atomsRef.current;
  const selectionValue = useMemo(() => ({ atoms }), [atoms]);
  const asideValue = useMemo(() => ({ atoms: atoms.aside }), [atoms]);
  return (
    <AsideContext.Provider value={asideValue}>
      <SelectionContext.Provider value={selectionValue}>{children}</SelectionContext.Provider>
    </AsideContext.Provider>
  );
};

export const useSelectedAll = () => {
  const { atoms } = useSelectionContext();
  return useAtomValue(atoms.selectedAllAtom);
};

export const useExcluded = () => {
  const { atoms } = useSelectionContext();
  return useAtomValue(atoms.excludedAtom);
};

export const useSelected = () => {
  const { atoms } = useSelectionContext();
  return useAtomValue(atoms.selectedAtom);
};

export const useSelect = () => {
  const { atoms } = useSelectionContext();
  return useSetAtom(atoms.selectAtom);
};

export const useIsActive = () => {
  const { atoms } = useSelectionContext();
  return useAtomValue(atoms.isActiveAtom);
};

export const useIsChecked = () => {
  const { atoms } = useSelectionContext();
  return useAtomValue(atoms.isCheckedAtom);
};

export const useSelectAll = () => {
  const { atoms } = useSelectionContext();
  return useSetAtom(atoms.selectAllAtom);
};

export const useResetSelection = () => {
  const { atoms } = useSelectionContext();
  return useSetAtom(atoms.resetSelectionAtom);
};

export const useActiveId = () => {
  const { atoms } = useSelectionContext();
  return useAtom(atoms.activeIdAtom);
};

export const useActivate = () => {
  const { atoms } = useSelectionContext();
  return useSetAtom(atoms.activateAtom);
};
